/*
  Buy box (T2, T8).
  - Option choices (colour swatches) pick the variant: updates the hidden id, the "Colour:" label,
    price, button state and the URL, and asks the gallery to show that variant's image.
  - Add to cart adds the robot, plus the add-on when its tickbox is ticked, through the cart panel (cart.js).
  - Buy now does the same, then goes straight to checkout.
  - The add-on tickbox and every listing button for the same add-on (filtration card, Complete your
    setup) are one state (site-behaviour-spec.md > Add-ons): the add-on goes into the cart with the robot.
    A listing for any other product adds it to the cart straight away. "Add both" adds the robot and its pair.
  - Any [data-add-main] button on the page (chapter bar, mobile buy bar, compare, final call to action)
    adds through this form, so the colour and add-on choices always apply.
*/
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.idInput = this.form.querySelector('input[name="id"]');
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.addButton = this.querySelector('[data-add]');
    this.buyNow = this.querySelector('[data-buy-now]');
    this.status = this.querySelector('[data-status]');

    this.querySelectorAll('[data-option] input').forEach((input) => {
      input.addEventListener('change', () => this.onOptionChange());
    });
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.add(false);
    });
    this.buyNow?.addEventListener('click', () => this.add(true));

    // Add-on: the tickbox and every listing button on the page share one state
    this.addonInput = this.querySelector('[data-addon-input]');
    this.addonButtons = [...document.querySelectorAll('[data-addon-toggle]')];
    this.addonInput?.addEventListener('change', () => this.syncAddon());
    this.addonButtons.forEach((btn) => btn.addEventListener('click', () => {
      if (this.addonInput && this.addonInput.value === btn.dataset.addonToggle) {
        this.addonInput.checked = !this.addonInput.checked;
        this.syncAddon();
      } else {
        // Not the buy-box add-on (an extra cross-sell): it goes straight into the cart
        this.addItems([{ id: Number(btn.dataset.addonToggle), quantity: 1 }], btn);
      }
    }));
    this.syncAddon();

    // Quantity stepper (accessory pages)
    this.querySelectorAll('[data-qty-step]').forEach((btn) => btn.addEventListener('click', () => {
      const input = this.querySelector('[data-quantity]');
      input.value = Math.min(99, Math.max(1, (Number(input.value) || 1) + Number(btn.dataset.qtyStep)));
    }));

    // "Add both" (pair section): the robot, with its colour choice, plus the paired product
    document.querySelectorAll('[data-add-pair]').forEach((btn) => btn.addEventListener('click', () => {
      if (this.addButton.disabled) return;
      this.addItems([{ id: Number(this.idInput.value), quantity: 1 }, { id: Number(btn.dataset.addPair), quantity: 1 }], btn);
    }));

    // Every other Add to cart on the page (chapter bar, mobile bar, compare, final call to action) uses this form
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-add-main]');
      if (!trigger || this.addButton.disabled) return;
      e.preventDefault();
      this.add(false, trigger);
    });
  }

  // Adds through the cart panel (assets/cart.js), which opens with "Added to your cart"
  async addItems(items, btn) {
    if (await window.BBCart?.add(items, btn)) this.status.textContent = this.dataset.addedMessage;
  }

  syncAddon() {
    const on = Boolean(this.addonInput?.checked);
    this.addonButtons.forEach((btn) => {
      if (!this.addonInput || this.addonInput.value !== btn.dataset.addonToggle) return;
      btn.setAttribute('aria-pressed', String(on));
      btn.textContent = on ? btn.dataset.added : btn.dataset.label;
    });
  }

  onOptionChange() {
    const chosen = [...this.querySelectorAll('[data-option]')].map((group) => group.querySelector('input:checked')?.value);
    const variant = this.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
    this.querySelectorAll('[data-option]').forEach((group, i) => {
      const label = group.querySelector('[data-option-value]');
      if (label) label.textContent = chosen[i];
    });
    if (!variant) return this.setButtons(false, this.dataset.unavailable);
    this.idInput.value = variant.id;
    document.querySelectorAll('[data-price]').forEach((el) => { el.textContent = variant.price_formatted; });
    this.setButtons(variant.available, variant.available ? this.dataset.addLabel : this.dataset.soldOut);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: variant.id, mediaId: variant.media_id } }));
  }

  setButtons(enabled, label) {
    this.addButton.disabled = !enabled;
    this.addButton.textContent = label;
    if (this.buyNow) this.buyNow.disabled = !enabled;
  }

  async add(checkout, trigger) {
    const qty = Math.max(1, Number(this.querySelector('[data-quantity]')?.value) || 1);
    const items = [{ id: Number(this.idInput.value), quantity: qty }];
    const addon = this.querySelector('[data-addon-input]');
    if (addon?.checked) items.push({ id: Number(addon.value), quantity: 1 });

    this.status.textContent = '';
    if (!checkout && window.BBCart) {
      if (await window.BBCart.add(items, trigger || this.addButton)) this.status.textContent = this.dataset.addedMessage;
      return;
    }
    const label = this.addButton.textContent;
    this.addButton.disabled = true;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Error');
      if (checkout) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
        return;
      }
      await this.updateCartCount();
      this.addButton.textContent = this.dataset.addedLabel;
      this.status.textContent = this.dataset.addedMessage;
      const triggerLabel = trigger?.innerHTML;
      if (trigger) trigger.textContent = this.dataset.addedLabel;
      setTimeout(() => {
        this.addButton.textContent = label;
        this.addButton.disabled = false;
        if (trigger) trigger.innerHTML = triggerLabel;
      }, 2500);
    } catch (err) {
      this.status.textContent = err.message;
      this.addButton.textContent = label;
      this.addButton.disabled = false;
    }
  }

  async updateCartCount() {
    try {
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (e) { /* the count catches up on the next page */ }
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
